import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { CheckDouble, Cross } from '@factorialco/f0-react/icons/app'
import {
  ProjectManagementCreateMissingLevelRatesDocument,
  ProjectManagementCreateMissingLevelRatesMutation,
  ProjectPeopleDataCollectionDocument,
  ProjectRatesDocument,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DEFAULT_CURRENCY } from 'modules/project_management/lib/constants'
import { ProjectManagementRateCreateInput } from 'modules/project_management/pages/ProjectDetailsPage/Employees/RatesSettingsSidepanel/types'

import LevelRatesList from './LevelRatesList'

interface RatesSettingsSidepanelProps {
  projectId: number
  isOpen: boolean
  isJobCatalogTreeEnabled: boolean
  onClose: () => void
}

const MissingLevelRatesSidepanel = ({
  isOpen,
  onClose,
  projectId,
  isJobCatalogTreeEnabled,
}: RatesSettingsSidepanelProps) => {
  const { data: projectRates } = useQuery(ProjectRatesDocument, {
    variables: { projectId },
  })

  const [createMissingLevelRates, { loading: runningQuery }] = useMutation(
    ProjectManagementCreateMissingLevelRatesDocument,
    {
      extractorFn: (data: ProjectManagementCreateMissingLevelRatesMutation) =>
        data.projectManagementMutations.bulkCreateProjectmanagementRate,
      refetchQueries: [ProjectRatesDocument, ProjectPeopleDataCollectionDocument],
      awaitRefetchQueries: true,
    }
  )

  const closeAndReset = () => {
    onClose()
    resetForm()
  }

  const { values, setValues, errors, handleSubmit, resetForm } = useForm<{
    rates: ProjectManagementRateCreateInput[]
  }>({
    defaultValues: {
      rates: [],
    },
    enableReinitialize: true,
    validate: (data) => {
      const errorValidation: {
        rates?: Record<string | number, Record<string, string>>
      } = {}

      data.rates.forEach((rate) => {
        const rateErrors: Record<string, string> = {}

        if (rate.billingRateCents === undefined) {
          rateErrors.billingRateCents = i18n.t(
            'project_management.forms.billing_rate.error.bill_rate_required'
          )
        }

        if (rate.profitabilityPercentageCents === undefined) {
          rateErrors.profitabilityPercentageCents = i18n.t(
            'project_management.forms.billing_rate.error.profitability_percentage_required'
          )
        }

        if (Object.keys(rateErrors).length > 0) {
          if (!errorValidation.rates) {
            errorValidation.rates = {}
          }
          errorValidation.rates[rate.resourceId] = rateErrors
        }
      })

      return errorValidation
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await createMissingLevelRates({
        variables: {
          rates: data.rates.map((rate) => ({
            projectId: projectId,
            resourceId: rate.resourceId,
            resourceType: rate.resourceType,
            billingRateCents: rate.billingRateCents,
            profitabilityPercentageCents: rate.profitabilityPercentageCents,
            currency: rate.currency,
          })),
        },
      })
      const onSubmitError = () => {
        setErrors(errors)
      }
      const handled = handleResult(result, (err) => {
        if (err.rates) errors.rates = i18n.t('common.error')
        onSubmitError()
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('project_management.forms.billing_rate.alerts.success'),
          time: 10000,
        })
        closeAndReset()
      }
      return handled
    },
  })

  const isAnyBillRateInvalid = values.rates.some(
    (rate) => rate.billingRateCents <= 0 || isNaN(rate.billingRateCents)
  )

  const existingRateIds =
    projectRates?.projectManagement.ratesConnection?.nodes
      .filter(
        (rate) =>
          rate.resourceType === 'JobCatalog::Level' || rate.resourceType === 'JobCatalog::TreeLevel'
      )
      .map((rate) => rate.resource?.id)
      .filter((id): id is number => id !== null && id !== undefined) ?? []

  const defaultJobLevelIds =
    projectRates?.projectManagement.project?.projectWorkers
      ?.map((worker) => {
        const referenceContract = worker.employee?.referenceContract
        return referenceContract?.jobCatalogLevel?.id || referenceContract?.jobCatalogTreeLevel?.id
      })
      .filter(
        (id): id is number => id !== null && id !== undefined && !existingRateIds.includes(id)
      ) ?? []

  const currency =
    projectRates?.projectManagement.project?.legalEntity?.currency ?? DEFAULT_CURRENCY
  return (
    <Sidepanel
      open={isOpen}
      onClose={closeAndReset}
      title={i18n.t('project_management.forms.billing_rate.header.define_bill_rates')}
      width="wide"
      boxPadding={false}
      footer={
        <Box gap="s8" flexDirection="row" width="full" justifyContent="flexEnd">
          <F0Button
            label={i18n.t('common.cancel')}
            onClick={closeAndReset}
            variant="critical"
            icon={Cross}
          />
          <F0Button
            label={i18n.t('common.save')}
            onClick={handleSubmit}
            variant="default"
            icon={CheckDouble}
            disabled={isAnyBillRateInvalid}
            loading={runningQuery}
          />
        </Box>
      }
    >
      <LevelRatesList
        isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
        currency={currency}
        missingRateIds={defaultJobLevelIds}
        rates={values.rates}
        onChange={(newRates) => setValues({ ...values, rates: newRates })}
      />
    </Sidepanel>
  )
}

export default MissingLevelRatesSidepanel
